import {
  Box,
  Column,
  Columns,
  Heading,
  Stack,
  Text,
} from 'braid-design-system';
import { forwardRef, type HTMLProps, type ReactElement } from 'react';
import * as styles from './VerificationDetailsTooltip.css';
import translations from './.vocab';
import { useTranslations } from '@vocab/react';
import type { Verification } from './types';
import Tippy from '@tippyjs/react';
import { CertsyBrandTooltipContent } from 'src/components/common/Certsy/CertsyTooltip/CertsyBrandTooltipContent';
import { IconCertified } from 'src/components/common/Certsy/Icons/IconCertified/IconCertified';
import { ProfileDetails } from '../Profile/ProfileDetails';

interface VerificationDetailsTooltipProps {
  verifications: Verification[];
  children: ReactElement;
}

export const VerificationDetailsTooltip: React.FC<
  VerificationDetailsTooltipProps
> = ({ verifications, children }) => {
  const { t } = useTranslations(translations);

  return (
    <Tippy
      render={(attrs) => (
        <div className={styles.verificationTooltip} {...attrs}>
          <Stack space="medium">
            <Box marginBottom="xsmall">
              <Columns space="xsmall" align="left" alignY="top">
                <Column width="content">
                  <Heading component="span" level="4">
                    <IconCertified />
                  </Heading>
                </Column>
                <Column>
                  <Columns alignY="center" space="none">
                    <Column>
                      <Heading component="span" level="4">
                        {t('Verified credentials')}
                      </Heading>
                    </Column>
                    <Box paddingRight="xsmall">
                      <Column width="content">
                        <CertsyBrandTooltipContent />
                      </Column>
                    </Box>
                  </Columns>
                </Column>
              </Columns>
            </Box>
            <Box
              display="flex"
              height="full"
              width="full"
              flexDirection="column"
              className={[styles.toolTipWidth, styles.desktopWidth]}
            >
              <Stack space="medium">
                {verifications.map((verification, index) => (
                  <Stack space="gutter" key={index}>
                    <Text
                      size="small"
                      weight="strong"
                      data={{
                        'hj-masked': true,
                      }}
                    >
                      {verification.title}
                    </Text>
                    <ProfileDetails
                      details={verification.credentialInfo.map(
                        ({ name, values }) => ({
                          name,
                          values,
                        }),
                      )}
                    />
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Stack>
        </div>
      )}
      interactive={true}
      placement="bottom-start"
      popperOptions={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 5],
            },
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['top-start', 'bottom-start'],
            },
          },
        ],
      }}
      delay={300}
      zIndex={200}
    >
      <TooltipWrapper>{children}</TooltipWrapper>
    </Tippy>
  );
};
const TooltipWrapper = forwardRef<HTMLSpanElement, HTMLProps<HTMLSpanElement>>(
  ({ children }, ref) => (
    <Box component="div">
      <Box component="span" ref={ref}>
        {children}
      </Box>
    </Box>
  ),
);
